import React, { useState } from 'react';
import { FlightSelect } from '@flybits/design-system';
import { saveContextualSchedulerAction } from 'store/rule/rule.action';
import { FullTime, Option, SelectOption } from 'types/common';
import { SchedulerStateItem } from 'store/rule/rule.type';
import { AbsoluteErrorReturnValue } from 'validators/ExperienceCanvas/rule.validator';
import { ScheduleDraftDispatch } from '../../scheduleDraft.types';
import { CLASSES } from '../classes';
import EndScheduleSection from '../EndScheduleSection/EndScheduleSection';
import EventSelectorSection from '../EventSelectorSection/EventSelectorSection';
import RepeatSection from '../RepeatSection/RepeatSection';
import '../ScheduleForm.scss';

type TEventsScheduleProps = {
  stepIdx: number;
  startScheduler?: SchedulerStateItem;
  endScheduler?: SchedulerStateItem;
  dateTime: { start: FullTime; end: FullTime; timezone: Option | undefined };
  errors: AbsoluteErrorReturnValue;
  draftDispatch: ScheduleDraftDispatch;
  setDateTime: React.Dispatch<
    React.SetStateAction<{
      start: FullTime;
      end: FullTime;
      timezone: Option | undefined;
    }>
  >;
};

const repeatOrEndScheduleOptions: SelectOption[] = [
  { key: 'off', name: 'Off' },
  { key: 'repeat-schedule', name: 'Repeat schedule' },
  { key: 'end-schedule', name: 'End Schedule' },
];
const MAIN_CLASS = 'events-schedule';

const EventsSchedule: React.FC<TEventsScheduleProps> = ({
  stepIdx,
  dateTime,
  errors,
  startScheduler,
  endScheduler,
  setDateTime,
  draftDispatch,
}) => {
  const getRepeatOrEndScheduleInitialValue = () => {
    if (Object.keys(endScheduler?.predicates || {}).length > 0 || !!dateTime.end.date) {
      return repeatOrEndScheduleOptions[2];
    }
    if (startScheduler?.frequencyType && startScheduler.frequencyType !== 'once') return repeatOrEndScheduleOptions[1];
    return repeatOrEndScheduleOptions[0];
  };

  const [repeatOrEndSchedule, setRepeatOrEndSchedule] = useState<SelectOption>(getRepeatOrEndScheduleInitialValue);

  const handleRepeatOrEndScheduleChange = (option: SelectOption) => {
    if (option.key === 'off' || option.key === 'repeat-schedule') {
      draftDispatch({
        type: 'UPDATE_STEP',
        payload: {
          stepIdx,
          fields: {
            omitEnd: true,
          },
        },
      });
      draftDispatch(
        saveContextualSchedulerAction({
          stepIdx,
          type: 'end',
          modifier: '',
          predicates: null,
          target: 'now',
          logic: '',
        }),
      );
      draftDispatch({
        type: 'CLEAR_SCHEDULE',
        payload: {
          stepIdx,
          type: 'end',
        },
      });
      setDateTime((prevState) => ({
        ...prevState,
        end: {
          isActive: false,
          date: undefined,
          time: undefined,
        },
      }));
    }
    setRepeatOrEndSchedule(option);
  };

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.SECTION}>
        <EventSelectorSection
          stepIdx={stepIdx}
          startOrEnd="start"
          scheduler={startScheduler}
          draftDispatch={draftDispatch}
        />
      </div>
      <div className={CLASSES.SECTION_SECONDARY}>
        <div className={CLASSES.FIELD}>
          Turn on Repeat Schedule/End Schedule
          <FlightSelect
            options={repeatOrEndScheduleOptions}
            handleOptionClick={handleRepeatOrEndScheduleChange}
            selected={repeatOrEndSchedule || repeatOrEndScheduleOptions[0]}
          />
        </div>
      </div>
      <RepeatSection
        stepIdx={stepIdx}
        startScheduler={startScheduler}
        endScheduler={endScheduler}
        hasEndDate={false}
        isEnabled={repeatOrEndSchedule?.key === 'repeat-schedule'}
        draftDispatch={draftDispatch}
      />
      <EndScheduleSection
        stepIdx={stepIdx}
        dateTime={dateTime}
        errors={errors}
        isEnabled={repeatOrEndSchedule?.key === 'end-schedule'}
        endScheduler={endScheduler}
        setDateTime={setDateTime}
        draftDispatch={draftDispatch}
      />
    </div>
  );
};

export default EventsSchedule;
